<template>
  <div>
    <table-toolbar
      :="isViewsSettingsHidden"
      :filters-count="filtersCount"
      :grid-data-loading="loadingStateValue"
      :headers="headers"
      :prepared-headers="preparedHeaders"
      :views-list="viewsList"
      :active-view-key="activeViewKey"
      :views-loading="viewsLoading"
      @reset-filters="resetFilters"
      @refresh="refresh"
      @change-active-view="$emit('active-view-changed', $event)"
      @save-edit-view="$emit('view-changed', $event)"
      @remove-view="$emit('remove-view', $event)"
    >
      <template>
        <slot name="toolbar" />
      </template>
    </table-toolbar>
    <slot
      :tableProps="tableProps"
      :tableListeners="tableListeners"
      :filtersProps="filtersProps"
      :filtersListeners="filtersListeners"
    />
    <component
      ref="remoteDataSource"
      v-if="dataSourceAdapterOptions"
      :is="dataSourceAdapterOptions.component"
      v-bind="dataSourceAdapterOptions.props"
      :initialSortBy="initialSortBy"
      @data-loaded="onRemoteDataLoaded"
    />
  </div>
</template>

<script>
import { get } from '@/utils'
import {
  config,
  dataSourceOptionsValidator,
  queryToState,
  stateToQuery,
  tableOptionsToState,
  stateToTableOptions,
  buildFilterCondition
} from './utils'
import { AxisGQL } from './data-sources'
import TableToolbar from './toolbar/table-toolbar'

export default {
  name: 'DataTableWrapper',
  components: { TableToolbar },
  props: {
    isViewsSettingsHidden: {
      type: Boolean,
      default: false
    },
    storedHeaders: {
      type: Array,
      default: () => []
    },
    viewsList: {
      type: Array,
      default: () => []
    },
    viewsLoading: {
      type: Boolean,
      default: false
    },
    activeViewKey: {
      type: [String, Number],
      default: ''
    },
    headers: {
      type: Array,
      default: () => []
    },
    multiSort: {
      type: Boolean,
      default: false
    },
    mustSort: {
      type: Boolean,
      default: true
    },
    initialSortBy: {
      type: [Array],
      default: () => []
    },
    initialFilters: {
      type: Object,
      default: () => ({})
    },
    dataSource: {
      type: Object,
      required: true,
      validator: dataSourceOptionsValidator
    },
    filters: {
      type: Object,
      default: () => ({})
    },
    options: {
      type: Object,
      default: () => ({ ...config })
    },
    forceWhere: {
      type: Object,
      default: () => ({})
    },
    queryParams: {
      type: Object,
      default: () => ({})
    },
    context: {
      type: Object,
      default: () => ({})
    }
  },
  data () {
    return {
      initialized: false,
      remoteData: {
        rows: [],
        totalRows: 0
      },
      preparedHeaders: []
    }
  },
  computed: {
    dataSourceType () {
      return this.dataSource.type
    },
    dataSourceAdapterOptions () {
      switch (this.dataSourceType) {
        case 'AxisGQL':
          return {
            component: AxisGQL,
            props: {
              forceWhere: this.forceWhere,
              query: this.dataSource.query,
              params: this.stateFromQueryString,
              processRows: this.dataSource.processRows || (rows => rows),
              queryParams: this.queryParams,
              context: this.context
            }
          }
        default:
          return null
      }
    },
    loadingStateValue () {
      switch (this.dataSourceType) {
        case 'PrismaGQL':
        case 'AxisGQL':
        case 'AxisREST':
        case 'custom':
          return (
            this.$refs.remoteDataSource && this.$refs.remoteDataSource.$apollo.loading
          )
        default:
          return false
      }
    },
    rows () {
      switch (this.dataSourceType) {
        case 'PrismaGQL':
        case 'AxisGQL':
        case 'AxisREST':
        case 'custom':
          return this.remoteData.rows
        default:
          return this.dataSource.rows // the local type case
      }
    },
    totalRows () {
      switch (this.dataSourceType) {
        case 'AxisGQL':
        case 'AxisREST':
        case 'PrismaGQL':
        case 'custom':
          return this.remoteData.totalRows
        default:
          return -1 // the local type case
      }
    },
    stateFromQueryString () {
      const state = queryToState(this.$route.query, this.wrapperConfig)
      // --------
      // @todo this condition used only in project Digital Primorye
      if (this.initialSortBy?.length === 0) {
        delete state?.sortBy
      }
      // -----
      this.$emit('query-state-updated', state)
      return state
    },
    tableOptions: {
      get () {
        return stateToTableOptions(this.stateFromQueryString, this.wrapperConfig)
      },
      set (val) {
        const stateFromTable = tableOptionsToState(val, this.wrapperConfig)
        const stateFromQueryString = this.stateFromQueryString
        const mergedState = !this.initialized
          ? { ...stateFromTable, ...stateFromQueryString, filters: this.initialFilters }
          : { ...stateFromQueryString, ...stateFromTable, filters: this.initialFilters }
        const initialSortBy = !this.initialized ? this.initialSortBy : []
        const newQuery = stateToQuery(mergedState, this.wrapperConfig, initialSortBy)
        const queryMerged = { ...this.$route.query, ...newQuery }
        this.$router.push({ query: queryMerged }).catch(() => {})
        if (!this.initialized) {
          this.$nextTick(() => {
            this.initialized = true
          })
        }
      }
    },
    tableProps () {
      return {
        items: this.rows,
        options: this.tableOptions,
        'multi-sort': this.multiSort,
        'must-sort': this.multiSort ? false : this.mustSort,
        'server-items-length': this.totalRows,
        loading: this.loadingStateValue,
        headers: this.filteredHeaders
      }
    },
    tableListeners () {
      return {
        'update:options': this.onOptionsUpdated
      }
    },
    wrapperConfig () {
      return {
        ...config,
        ...this.options,
        filters: this.filters
      }
    },
    filteredHeaders () {
      // if (!this.$scopedSlots.default) return []
      // const tableVnode = this.$scopedSlots.default()[0]
      // if (!tableVnode || tableVnode.componentOptions.tag !== 'v-data-table') {
      //   throw new Error('DataTableWrapper: v-data-table component not found in the default slot at position 0')
      // }
      // return tableVnode.componentOptions.propsData.headers
      return this.headers.filter(header => this.preparedHeaders.includes(header.value))
    },
    filtersProps () {
      return {
        headers: this.filteredHeaders,
        values: this.stateFromQueryString.filters || {},
        filters: this.filters,
        options: {
          ...config,
          ...this.options
        }
      }
    },
    filtersListeners () {
      return {
        update: this.setFilters
      }
    },
    filtersCount () {
      const filterList = Object.keys(get(this.stateFromQueryString, 'filters', {}))
      if (this.initialFilters) {
        return filterList.filter(
          filterName => !Object.keys(this.initialFilters).includes(filterName)
        ).length
      }
      return filterList.length
    }
  },
  watch: {
    storedHeaders () {
      this.prepareHeaders()
    }
  },
  methods: {
    prepareHeaders () {
      this.resetHeaders()
      if (this.storedHeaders.length) this.preparedHeaders = this.storedHeaders
    },
    resetHeaders () {
      this.preparedHeaders = this.headers.map(headerItem => headerItem.value)
    },
    onOptionsUpdated (options) {
      this.tableOptions = options
    },
    onRemoteDataLoaded ({ rows, totalRows }) {
      this.remoteData = { rows, totalRows }
      this.$emit('data-loaded')
    },
    setFilters (filters) {
      const query = JSON.parse(JSON.stringify(this.$route.query))
      filters.forEach(({ field, modifier, value }) => {
        const condition = buildFilterCondition(field, modifier, value, this.wrapperConfig)
        if (!condition.value) delete query[`${this.wrapperConfig.prefix}${condition.key}`]
        else query[`${this.wrapperConfig.prefix}${condition.key}`] = condition.value
      })
      query[`${this.wrapperConfig.prefix}${this.wrapperConfig.pageParam}`] = 1
      this.$router.push({ query }).catch(() => {})
    },
    resetFilters () {
      const currentFilters = get(this.stateFromQueryString, 'filters', {})
      const filters = Object.entries(currentFilters)
        .filter(([filterName, _]) => {
          if (this.initialFilters) {
            return !Object.keys(this.initialFilters).includes(filterName)
          }
          return true
        })
        .reduce((acc, [field, rules]) => {
          Object.keys(rules).forEach(modifier => {
            acc.push({ field, modifier, value: null })
          })
          return acc
        }, [])

      this.setFilters(filters)
    },
    refresh () {
      return this.$refs.remoteDataSource.refetch()
    }
  },
  created () {
    this.prepareHeaders()
  }
}
</script>
